import { useEffect, useId, useRef, useState, type ReactElement } from 'react';

import { ModalShell } from '@/components/ui/ModalShell';
import { businessToday, formatDate } from '@/lib/time';

interface Props {
  open: boolean;
  busy?: boolean;
  onCancel: () => void;
  onConfirm: (effectiveDate: string) => void;
  /** La API pidió re-autenticación (step-up): se ofrece confirmar identidad. */
  stepUpRequired?: boolean;
  onReauthenticate?: () => void;
  /** G2 (A-04): vigencia que fijó la primera firma de la ronda (`AAAA-MM-DD`); las
   *  siguientes firman la misma y no la pueden cambiar. */
  fixedDate?: string | null;
  /** G4: vigencia sugerida (hoy + plazo de capacitación, `AAAA-MM-DD`). */
  suggestedDate?: string | null;
}

/** Firma de aprobación: fija la fecha de entrada en vigencia (el sistema activa el
 *  documento en esa fecha). La versión en aprobación ya no admite cambios; la última firma
 *  la aprueba. */
export function ApproveModal({
  open,
  busy = false,
  onCancel,
  onConfirm,
  stepUpRequired = false,
  onReauthenticate,
  fixedDate = null,
  suggestedDate = null,
}: Props): ReactElement | null {
  const [date, setDate] = useState('');
  // G4: la persona ya eligió una fecha; una sugerencia que llega o cambia después no la pisa.
  const [touched, setTouched] = useState(false);
  // G2-D: la fecha de la ronda que había fijado otra firma (para notar que se reinició).
  const prevFixed = useRef<string | null>(null);
  const titleId = useId();
  useEffect(() => {
    // Valor inicial de la fecha (único lugar). G2 (A-04): la de la ronda si ya la fijó la
    // primera firma. G4: si no, la vigencia sugerida (si no es pasada), sin pisar una
    // fecha que la persona ya eligió. Al cerrar, vacía.
    const suggested = (): string => {
      const today = businessToday(new Date());
      return suggestedDate !== null && suggestedDate >= today ? suggestedDate : '';
    };
    if (!open) {
      setDate('');
      setTouched(false);
      prevFixed.current = null;
      return;
    }
    if (fixedDate !== null) {
      setDate(fixedDate);
      prevFixed.current = fixedDate;
      return;
    }
    // G2-D: la ronda se reinició con el modal abierto (la fecha fijada volvió a null). La
    // fecha de la ronda vieja no la eligió la persona: se vuelve a la sugerida.
    if (prevFixed.current !== null) {
      prevFixed.current = null;
      setTouched(false);
      setDate(suggested());
      return;
    }
    if (!touched) {
      setDate(suggested());
    }
  }, [open, fixedDate, suggestedDate, touched]);

  if (!open) {
    return null;
  }
  // Previene elegir una fecha pasada en el campo (el backend además la rechaza). Día de
  // negocio, el mismo que valida el backend (no UTC ni la zona del navegador).
  const today = businessToday(new Date());
  return (
    <ModalShell
      open={open}
      title="Firmar y aprobar"
      busy={busy}
      onClose={onCancel}
      footer={
        <>
          <button type="button" className="btn mut sm" onClick={onCancel} disabled={busy}>
            Cancelar
          </button>
          {stepUpRequired ? (
            <button type="button" className="btn sm" onClick={() => onReauthenticate?.()}>
              Confirmar identidad
            </button>
          ) : (
            <button
              type="button"
              className="btn sm"
              onClick={() => onConfirm(date)}
              disabled={busy || date === ''}
            >
              {busy ? 'Firmando…' : 'Firmar y aprobar'}
            </button>
          )}
        </>
      }
    >
      <div className="modal-body">
        <label htmlFor={`${titleId}-d`} className="fld-lab">
          Fecha de entrada en vigencia
        </label>
        <input
          id={`${titleId}-d`}
          type="date"
          value={date}
          min={fixedDate === null ? today : undefined}
          disabled={fixedDate !== null}
          onChange={(e) => {
            setTouched(true);
            setDate(e.target.value);
          }}
          className="fld-full"
        />
        {fixedDate !== null && (
          <p className="soft fld-hint">La fijó la primera firma de esta aprobación.</p>
        )}
        {/* G2-D: la vigencia fijada ya llegó en el día de negocio; la última firma la activa
            en el acto y la capacitación vence ese día (la regla la decide Grupo DIM; A-02 rige). */}
        {fixedDate !== null && fixedDate <= today && (
          <p role="alert" className="fld-warn">
            La vigencia fijada por la primera firma ({formatDate(fixedDate)}) ya llegó: con la
            última firma, el documento entra en vigencia de inmediato y su capacitación vence ese
            día.
          </p>
        )}
        {fixedDate === null && suggestedDate !== null && date === suggestedDate && (
          <p className="soft fld-hint fld-gap">
            Sugerida según el plazo de capacitación: las lecturas vencen ese día.
          </p>
        )}
        <p className="soft fld-fine">
          La versión ya no admite cambios: todos los aprobadores firman el mismo contenido. Con la
          última firma queda aprobada y entra en vigencia en la fecha indicada.
        </p>
        {stepUpRequired && (
          <p role="alert" className="fld-err fld-fine">
            Firmar requiere una autenticación reciente. Inicia sesión de nuevo y vuelve a firmar.
          </p>
        )}
      </div>
    </ModalShell>
  );
}
